import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormBuilder, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { CampaignDetailsService } from '../../../../services/campaign/campaign-details.service';
import { CampaignStepperService } from '../../../../services/campaign/campaign-stepper.service';
import { ProcessCampaignService } from '../../../../services/campaign/process-campaign.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CancelCampaignService } from '../../../../services/campaign/cancel-campaign.service';

@Component({
  selector: 'app-campaign-details-form',
  standalone: true,
  imports: [FormWrapperComponent, InputFieldComponent, MatTooltipModule, ReactiveFormsModule],
  templateUrl: './campaign-details-form.component.html',
  styleUrl: '../campaign-form.css',
})
export class CampaignDetailsFormComponent {
  campaignDetailsService = inject(CampaignDetailsService);
  campaignStepperService = inject(CampaignStepperService);
  processCampaignService = inject(ProcessCampaignService);
  cancelCampaignService = inject(CancelCampaignService);
  router = inject(Router);
  route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  constructor(private fb: FormBuilder) {}

  campaignDetailForm = this.fb.group({
    campaignName: ['', Validators.required],
    description: ['', Validators.required],
    goals: this.fb.array([this.fb.control('', Validators.required)]),
  });

  submitted = false;

  get campaignName() {
    return this.campaignDetailForm.get('campaignName');
  }

  get description() {
    return this.campaignDetailForm.get('description');
  }

  get goals() {
    return this.campaignDetailForm.get('goals') as FormArray;
  }

  addGoal() {
    this.goals.push(this.fb.control('', Validators.required));
  }

  removeGoal(index: number) {
    // Service first: removing the control re-syncs the service from the form.
    this.campaignDetailsService.removeGoal(index);
    this.goals.removeAt(index);
  }

  /** Continue stays pressable: an incomplete form explains itself instead. */
  handleSubmit() {
    this.submitted = true;
    if (this.campaignDetailForm.invalid) return;
    this.syncService();
    this.processCampaignService.campaignDetails = this.campaignDetailForm.value;
    this.campaignStepperService.stepCompleted('campaign-details');
    this.campaignStepperService.moveToNextStep('campaign-details');
  }

  /** Copies the form into the wizard state, so the summary beside it is live. */
  private syncService() {
    const value = this.campaignDetailForm.getRawValue();
    this.campaignDetailsService.campaignDetails.campaignName = value.campaignName ?? '';
    this.campaignDetailsService.campaignDetails.description = value.description ?? '';
    this.campaignDetailsService.campaignDetails.goals = (value.goals ?? []).map((goal) => String(goal ?? ''));
  }

  ngOnInit() {
    this.campaignDetailForm.patchValue({
      campaignName: this.campaignDetailsService.campaignDetails.campaignName,
      description: this.campaignDetailsService.campaignDetails.description,
    });

    if (this.campaignDetailsService.campaignDetails.goals.length > 0) {
      this.goals.clear();
      this.campaignDetailsService.campaignDetails.goals.forEach((goal) => {
        // Restored goals stay required, like new ones.
        this.goals.push(this.fb.control(goal, Validators.required));
      });
    }

    // Live summary: the (change) handlers above only fire on blur.
    this.campaignDetailForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncService());
  }
}
